import { navigate, RouteComponentProps } from '@reach/router';
import { SET_SEARCH_CRITERIA } from 'actions/SearchCriteria/searchCriteriaActionTypes';
import { DevXCalendarObject } from 'components/DevXCalendarControl/DevXCalendarObject';
import DropdownControl from 'components/DropDown/DropdownControl';
import DropDownControlCodeTableRow from 'components/DropDown/DropDownControlCodeTable/DropDownControlCodeTableRow';
import DropdownControlSqlRow from 'components/DropDown/DropDownControlSql/DropDownControlSqlRow';
import { MethodType } from 'components/DropDown/MethodType';
import GridControl, { calcGridInspectorWidth } from 'components/GridControl';
import { InspectorSearch } from 'components/InspectorSearch/InspectorSearch';
import PageControl from 'components/PageControl/PageControl';
import TextboxControlRow from 'components/TextboxControl/TextboxControlRow';
import { fetchData, IDataObject } from 'data/DataConnector';
import { setEntityId } from 'helpers/AuthenticationHelper';
import {
  OnChange,
  OnChangeWithValidation,
  OnDateBlur,
  OnSaveValidation,
  ValidationModel,
  ValidationType,
} from 'helpers/ValidationHelper';
import React, { useEffect, useReducer, useRef } from 'react';
import { connect } from 'react-redux';
import { ControllerNameEnum } from 'util/enums/Enum';
import { InlineRowComponent, RangeStyledComponentRow } from 'util/helpers/CommonComponents';
import { getTrimmedValue, getUsername } from 'util/helpers/helperFunctions';
import 'views/DealList/DealList.css';
import SettingValueEdit from 'views/Includes/SettingValueEdit';
import SelectDealVCM from './SelectDealVCM';
import SelectDealVDA from './SelectDealVDA';

const DEAL_LIST_HOME_PAGE: string = 'DEAL_LIST_HOME_PAGE';

/*
We can use IProps Interface, this is custom page specific handling.
*/
type Props = {
  name?: string;
  searchCriteriaDict: any;
  changeSearchCriteria: any;
  isBackClicked: boolean;
} & RouteComponentProps<{}>;

/*
State Interface
*/
interface IState {
  isLoading: boolean;
  showSearch: boolean;
  dealListData: IDataObject;
  searchCriteria: {
    cId: string;
    closingDateBegin?: Date;
    closingDateEnd?: Date;
    dealName: string;
    dealType: string;
    entityStatus: string;
    entityStatusDescription: string;
    luId: string;
    reinvestmentPeriodDate?: Date;
    reinvestmentPeriodDateCompare: string;
    tId: string;
    userId: string;
  };
  editParameters: any;
  showDataAnalystEdit: boolean;
  showDirectorEdit: boolean;
  showSettingValueEdit: boolean;
  configCode: string;
  entityId: number;
}
const DealList: React.FC<Props> = (props) => {
  const searchCriteriaRef = useRef({
    'Entity Status': 'Active',
  });

  const reinvestmentPeriodDateData = [
    { value: '', description: '(None)' },
    { value: 'On or Before', description: 'On or Before' },
    { value: 'On or After', description: 'On or After' },
    { value: 'Equals', description: 'Equals' },
  ];

  const validationDataRef = useRef({
    closingDateBegin: new ValidationModel([ValidationType.ValidDate], 'Closing date between From', true),
    closingDateEnd: new ValidationModel([ValidationType.ValidDate], 'Closing date between To', true),
    dealName: new ValidationModel([ValidationType.MaxLength], 'Deal Name', true, { maxLength: 50 }),
    reinvestmentPeriodDate: new ValidationModel([ValidationType.ValidDate], 'Reinvestment period date', true),
  });

  const initialState = () => {
    let entityStatus = 'A';
    let entityStatusDescription = 'Active';

    if (DEAL_LIST_HOME_PAGE in props.searchCriteriaDict && props.isBackClicked) {
      entityStatus = props.searchCriteriaDict.DEAL_LIST_HOME_PAGE.searchCriteria.entityStatus;
      entityStatusDescription = props.searchCriteriaDict.DEAL_LIST_HOME_PAGE.searchCriteria.entityStatusDescription;
    }

    return {
      configCode: '',
      dealListData: { data: [], schema: [] },
      editParameters: {},
      entityId: 0,
      isLoading: false,
      searchCriteria: {
        cId: '',
        dealName: '',
        dealType: '',
        entityStatus,
        entityStatusDescription,
        luId: '',
        reinvestmentPeriodDateCompare: '',
        tId: '',
        userId: getUsername(),
      },
      showDataAnalystEdit: false,
      showDirectorEdit: false,
      showSearch: !props.isBackClicked || !(DEAL_LIST_HOME_PAGE in props.searchCriteriaDict),
      showSettingValueEdit: false,
    };
  };

  const reducer = (prev: IState, update: Partial<IState> | ((state: IState) => Partial<IState>)) => {
    const nextUpdate = typeof update === 'function' ? update(prev) : update;
    return { ...prev, ...nextUpdate };
  };

  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const stateRef = useRef(state);

  const setState = (update: Partial<IState> | ((prev: IState) => Partial<IState>)) => {
    dispatch(update);
  };

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const getComponentProxy = () => ({ state: stateRef.current, setState });

  const onChangeHandler = (event: any) => OnChange(getComponentProxy(), stateRef.current.searchCriteria, event);

  const onChangeWithValidationHandler = (event: any, allowLiteralNull: boolean = false) =>
    OnChangeWithValidation(
      getComponentProxy(),
      stateRef.current.searchCriteria,
      validationDataRef.current,
      event,
      allowLiteralNull,
    );

  const onDateBlurHandler = (value: any, name: any) =>
    OnDateBlur(getComponentProxy(), stateRef.current.searchCriteria, validationDataRef.current, value, name);

  const genericChangeHandler = (event: any, name?: string, data?: any, allowLiteralNull: boolean = false) => {
    let nameString;
    let value;
    if (data) {
      if (name && name.includes(';')) {
        nameString = name.split(';')[0];
        const ev = {
          target: { name: name.split(';')[1], value: data.selectedText },
        };
        onChangeHandler(ev);
      } else {
        nameString = name;
      }
      value = event;
    } else if (name !== undefined && typeof event === 'object' && 'target' in event && 'checked' in event.target) {
      nameString = event.target.name;
      value = event.target.checked;
    } else if (name) {
      nameString = name;
      value = event;
    } else if ('target' in event) {
      nameString = event.target.name;
      nameString = nameString.includes(';') ? nameString.split(';')[0] : nameString;
      value = event.target.value;
      if (
        'nativeEvent' in event &&
        'target' in event.nativeEvent &&
        'selectedIndex' in event.nativeEvent.target &&
        event.target.name.includes(';')
      ) {
        const index = event.nativeEvent.target.selectedIndex;
        const selectedText = event.nativeEvent.target[index].text;
        const selectedName = event.target.name.split(';')[1];
        const ev = { target: { name: selectedName, value: selectedText } };
        onChangeHandler(ev);
      }
    } else if ('value' in event) {
      nameString = event.name;
      value = event.value;
    }
    const e = { target: { name: nameString, value } };
    if (nameString in validationDataRef.current) {
      onChangeWithValidationHandler(e, allowLiteralNull);
    } else {
      onChangeHandler(e);
    }
  };

  const loadData = async (updateGlobalState: boolean = true) => {
    setState({ isLoading: true });
    searchCriteriaRef.current['Entity Status'] = stateRef.current.searchCriteria.entityStatusDescription;

    const result = await fetchData(ControllerNameEnum.Sql, 'EntityList', MethodType.Post, stateRef.current.searchCriteria);
    setState({ dealListData: result.dataObject, isLoading: false });
    if (updateGlobalState) {
      const searchCriteria = {
        entityStatus: stateRef.current.searchCriteria.entityStatus,
        entityStatusDescription: stateRef.current.searchCriteria.entityStatusDescription,
      };
      await props.changeSearchCriteria(searchCriteria, searchCriteriaRef.current);
    }
  };

  const onDNClicked = (dataRow: any, event: any) => {
    event.preventDefault();
    const entityId: string = dataRow.entity_id;
    navigate('/DealSetup/DealInfo/Main/Entity/entityId/' + entityId);
  };

  const onDirClicked = (dataRow: any, event: any) => {
    event.preventDefault();
    setState({
      editParameters: {
        dealName: getTrimmedValue(dataRow.deal_nickname),
        entityId: Number(getTrimmedValue(dataRow.entity_id)),
      },
      showDirectorEdit: true,
    });
  };

  const onDAClicked = (dataRow: any, event: any) => {
    event.preventDefault();
    setState({
      editParameters: {
        dealName: getTrimmedValue(dataRow.deal_nickname),
        entityId: Number(getTrimmedValue(dataRow.entity_id)),
      },
      showDataAnalystEdit: true,
    });
  };

  const onGDDClicked = (dataRow: any, event: any) => {
    event.preventDefault();
    setState({
      configCode: 'global_data_deal',
      entityId: Number(getTrimmedValue(dataRow.entity_id)),
      showSettingValueEdit: true,
    });
  };

  const onCAClicked = (dataRow: any, event: any) => {
    event.preventDefault();
    setState({
      configCode: 'contact_agent',
      entityId: Number(getTrimmedValue(dataRow.entity_id)),
      showSettingValueEdit: true,
    });
  };

  const onSearchClicked = async () => {
    if (await OnSaveValidation(getComponentProxy(), stateRef.current.searchCriteria, validationDataRef.current)) {
      setState({
        showSearch: false,
      });
      loadData();
    }
  };

  const onSearchClose = () => {
    setState({
      showSearch: false,
    });
  };

  const onSearchOpen = () => {
    setState({
      showSearch: true,
    });
  };

  const onModalClose = () => {
    setState({
      showDataAnalystEdit: false,
      showDirectorEdit: false,
      showSettingValueEdit: false,
    });
  };

  const onSuccess = async () => {
    await onModalClose();
    loadData(false);
  };

  useEffect(() => {
    const initialize = async () => {
      if (DEAL_LIST_HOME_PAGE in props.searchCriteriaDict && props.isBackClicked) {
        await setEntityId(0);
        loadData(false);
      } else {
        await setEntityId(0);
        onSearchOpen();
      }
    };
    initialize();
  }, []);

  const linkedColumns = [
    { columnName: 'Deal Nickname', onClick: onDNClicked },
    { columnName: 'Director', onClick: onDirClicked },
    { columnName: 'Data Analyst', onClick: onDAClicked },
    { columnName: 'Global Data Deal', onClick: onGDDClicked },
    { columnName: 'Contact Agent', onClick: onCAClicked },
  ];

  return (
    <PageControl
      pageTitle="Deal List"
      showSearch={state.showSearch}
      searchCriteria={searchCriteriaRef.current}
      onSearchClose={onSearchClose}
      onSearchOpen={onSearchOpen}
      pageWidth={state.showSearch ? calcGridInspectorWidth() : undefined}
      inspectorTitle="Search Deals"
      inspectorChildren={
        <InspectorSearch
          showSearchFilter={state.showSearch}
          onSearchFilterClose={onSearchClose}
          filterHeader="Search Deals"
          onSearchClicked={onSearchClicked}
        >
          <table>
            <tbody>
              <TextboxControlRow
                name="dealName"
                label="Deal Name"
                isReadonly={false}
                isRequired={false}
                value={state.searchCriteria.dealName}
                isValid={validationDataRef.current.dealName.IsValid}
                validationMessage={validationDataRef.current.dealName.Message}
                onChange={onChangeWithValidationHandler}
              />
              {RangeStyledComponentRow(
                <DevXCalendarObject
                  isReadonly={false}
                  isRequired={false}
                  name="closingDateBegin"
                  value={state.searchCriteria.closingDateBegin}
                  isValid={validationDataRef.current.closingDateBegin.IsValid}
                  onBlur={onDateBlurHandler}
                  validationMessage={validationDataRef.current.closingDateBegin.Message}
                  onChange={genericChangeHandler}
                />,
                <DevXCalendarObject
                  isReadonly={false}
                  isRequired={false}
                  name="closingDateEnd"
                  value={state.searchCriteria.closingDateEnd}
                  isValid={validationDataRef.current.closingDateEnd.IsValid}
                  onBlur={onDateBlurHandler}
                  validationMessage={validationDataRef.current.closingDateEnd.Message}
                  onChange={genericChangeHandler}
                />,
                'Closing date between',
                'From',
              )}
              <DropDownControlCodeTableRow
                label="Deal Type"
                name="dealType"
                value={state.searchCriteria.dealType}
                codeType="DEAL_TYPE"
                selectOption="(No Preference)"
                onChange={onChangeHandler}
              />
              <DropdownControlSqlRow
                selectOption="(No Preference)"
                displayField="legal_name"
                valueField="agent_id"
                name="cId"
                methodRoute="Sql/DealAgentLookup"
                methodType={MethodType.Get}
                parameters="C"
                label="Collateral Manager"
                value={state.searchCriteria.cId}
                onChange={onChangeHandler}
              />
              <DropdownControlSqlRow
                selectOption="(No Preference)"
                displayField="legal_name"
                valueField="agent_id"
                name="luId"
                methodRoute="Sql/DealAgentLookup"
                methodType={MethodType.Get}
                parameters="LU"
                label="Lead Underwriter"
                value={state.searchCriteria.luId}
                onChange={onChangeHandler}
              />
              <DropdownControlSqlRow
                selectOption="(No Preference)"
                displayField="legal_name"
                valueField="agent_id"
                name="tId"
                methodRoute="Sql/DealAgentLookup"
                methodType={MethodType.Get}
                parameters="T"
                label="Trustee"
                value={state.searchCriteria.tId}
                onChange={onChangeHandler}
              />
              {InlineRowComponent(
                <DropdownControl
                  data={reinvestmentPeriodDateData}
                  displayField="description"
                  name="reinvestmentPeriodDateCompare"
                  valueField="value"
                  value={state.searchCriteria.reinvestmentPeriodDateCompare}
                  onChange={onChangeHandler}
                />,
                <DevXCalendarObject
                  isReadonly={false}
                  isRequired={false}
                  name="reinvestmentPeriodDate"
                  value={state.searchCriteria.reinvestmentPeriodDate}
                  isValid={validationDataRef.current.reinvestmentPeriodDate.IsValid}
                  onBlur={onDateBlurHandler}
                  validationMessage={validationDataRef.current.reinvestmentPeriodDate.Message}
                  onChange={genericChangeHandler}
                />,
                'Reinvestment period date',
                'dealListReinvestmentDiv',
              )}
              <DropDownControlCodeTableRow
                label="Entity Status"
                name="entityStatus;entityStatusDescription"
                dataTestId="entityStatus"
                value={state.searchCriteria.entityStatus}
                codeType="ENTITY_STATUS"
                selectOption="All"
                isRequired={false}
                isReadonly={false}
                onChange={genericChangeHandler}
              />
            </tbody>
          </table>
        </InspectorSearch>
      }
    >
      <GridControl
        dataObject={state.dealListData}
        enableEditableButton={false}
        isLoading={state.isLoading}
        linkedColumns={linkedColumns}
      />

      {state.showDirectorEdit && (
        <SelectDealVCM params={state.editParameters} onClose={onModalClose} onSave={onSuccess} />
      )}
      {state.showDataAnalystEdit && (
        <SelectDealVDA params={state.editParameters} onClose={onModalClose} onSave={onSuccess} />
      )}
      {state.showSettingValueEdit && (
        <SettingValueEdit
          configCode={state.configCode}
          systemMode={'DEAL'}
          entityId={state.entityId}
          onClose={onModalClose}
          onSave={onSuccess}
        />
      )}
    </PageControl>
  );
};

const mapStateToProps = (stateFromStore: any) => {
  return {
    isBackClicked: stateFromStore.searchCriteriaRdcr.isBackClicked,
    searchCriteriaDict: stateFromStore.searchCriteriaRdcr.searchCriteriaDict,
  };
};

const mapDispatchActionToProps = (dispatch: any) => {
  return {
    changeSearchCriteria: (searchCriteria: any, searchCriteriaStateObj: any) =>
      dispatch({
        type: SET_SEARCH_CRITERIA,
        data: {
          searchTab: DEAL_LIST_HOME_PAGE,
          searchCriteria,
          searchCriteriaStateObj,
        },
      }),
  };
};

export default connect(mapStateToProps, mapDispatchActionToProps)(DealList);
